import React, { useEffect, useState } from "react";
import { Newspaper, Download, Share2 } from "lucide-react";
import { toast } from "sonner";
import { api, BACKEND_URL } from "../lib/api";
import { HeadlinePicker } from "./HeadlinePicker";
import { SocialShare } from "./SocialShare";

async function toFile(url, name) {
  const r = await fetch(url);
  if (!r.ok) throw new Error("render failed");
  const b = await r.blob();
  return new File([b], name, { type: b.type || "image/png" });
}

function download(file) {
  const a = document.createElement("a");
  a.href = URL.createObjectURL(file);
  a.download = file.name;
  a.click();
}

// Exports 2 files: the TMZ-style news card first, then the regular photo.
export async function exportBoth(cardUrl, photoUrl, base = "wedding") {
  const files = [await toFile(cardUrl, `${base}-breaking-news.png`), await toFile(photoUrl, `${base}-photo.jpg`)];
  if (navigator.canShare?.({ files })) {
    try { await navigator.share({ files, title: "Breaking wedding news" }); return "shared"; } catch (e) { if (e?.name === "AbortError") return "cancelled"; }
  }
  files.forEach(download);
  toast.success("Downloaded the news card and the photo");
  return "downloaded";
}

export function NewsShareCard({ slug, item, defaultHeadline }) {
  const [headline, setHeadline] = useState(item.caption || defaultHeadline);
  const [layout, setLayout] = useState("square");
  const [preview, setPreview] = useState("");
  const [busy, setBusy] = useState(false);
  const base = `${BACKEND_URL}/api/public/weddings/${slug}/memories/${item.item_id}`;
  const card = `${base}/news.png?token=${item.share_token}&layout=${layout}&headline=${encodeURIComponent(headline)}`;
  useEffect(() => { const t = setTimeout(() => setPreview(card), 500); return () => clearTimeout(t); }, [card]);
  const markShared = () => api.post(`/public/weddings/${slug}/memories/${item.item_id}/shared`, { token: item.share_token, headline }).catch(() => {});
  async function share() {
    setBusy(true);
    markShared();
    try { await exportBoth(card, `${base}/photo?token=${item.share_token}`, item.item_id); } catch { toast.error("Couldn't make the news card"); } finally { setBusy(false); }
  }
  return (
    <div className="bg-white border border-[var(--line)] rounded-2xl p-4 text-left space-y-3" data-testid={`news-share-${item.item_id}`}>
      <p className="text-[11px] uppercase tracking-[0.2em] text-[var(--gold)] flex items-center gap-1.5"><Newspaper className="w-3.5 h-3.5" /> Breaking news edition</p>
      {preview && <img src={preview} alt="News card preview" className="w-full rounded-xl bg-black" data-testid={`news-preview-${item.item_id}`} />}
      <input value={headline} maxLength={120} onChange={(e) => setHeadline(e.target.value)} placeholder="Write your headline" className="input-luxe" data-testid={`news-headline-${item.item_id}`} />
      <HeadlinePicker slug={slug} item={item} onPick={(h) => setHeadline(h.slice(0, 120))} />
      <div className="flex gap-2">
        {[["square", "Post"], ["vertical", "Story"]].map(([k, l]) => <button key={k} onClick={() => setLayout(k)} className={`px-3 py-1.5 rounded-full text-xs border whitespace-nowrap ${layout === k ? "bg-[var(--ink)] text-white border-[var(--ink)]" : "border-[var(--line)]"}`} data-testid={`news-layout-${k}-${item.item_id}`}>{l}</button>)}
        <button onClick={share} disabled={busy} className="btn-gold ml-auto !py-2 text-xs" data-testid={`news-share-btn-${item.item_id}`}>{busy ? "Preparing…" : <><Share2 className="w-3.5 h-3.5" /> Share</>}</button>
      </div>
      <SocialShare url={card} text={headline} id={item.item_id} onShare={markShared} />
      <p className="text-[11px] text-[var(--muted)] flex items-center gap-1"><Download className="w-3 h-3" /> On your phone, Share opens Instagram, Facebook, TikTok and any app you have. You get 2 images: the news card, then your regular photo. Shared cards also play on the party TV.</p>
    </div>
  );
}
